import { createClass as _createClass, toConsumableArray as _toConsumableArray, classCallCheck as _classCallCheck, defineProperty as _defineProperty, slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import lodash from 'lodash-es';

var AgGridNavigationAdapter = /*#__PURE__*/function () {
  function AgGridNavigationAdapter() {
    _classCallCheck(this, AgGridNavigationAdapter);
    /**
     * keyboard navigation adapter for ag grid
     * grids must be a single tab stop
     * retaining the last cell or control focused
     * this file brings AG grid into compliance with WAI WCAG 2.2 AA standard
     * please see https://www.w3.org/WAI/ARIA/apg/patterns/grid/ for deatils
     SAMPLUE USAGE:
     import AgGridNavigationAdapter from './IlDataTableV3NavigationAdapter';
    const navigationAdapter = new AgGridNavigationAdapter();
     // include the following in your AgGridReact jsx data props:
    onFirstDataRendered = {navigationAdapterRef?.current?.initialize}
    onComponentStateChanged = {navigationAdapterRef?.current?.update}
    onSelectionChanged = {navigationAdapterRef?.current?.rowSelectionHandler}
    onViewportChanged = {navigationAdapterRef?.current?.resetContentTabIndicesDebounced}
    tabToNextCell = {() => null}
     ***be sure .ag-tab-guard elements are set to display none in CSS
    as they interfere with proper tab sequencing
     *
    * @class AgGridNavigationAdapter
    */
    /**
     * the time between executions of a debounced function
     * @property {number} debounceDelay
     */
    _defineProperty(this, "debounceDelay", 1000);
    /**
     * selector for controls within the grid
     * @property {string} controlSelector
     */
    _defineProperty(this, "controlSelector", 'a, input, select, button, textarea, [role=link], [role=checkbox], [role=button], [role=listbox], [tabindex]');
    /**
     * the single tabbable element within the grid
     * @property {Element} focusEl
     */
    _defineProperty(this, "focusEl", void 0);
    /**
     * the grid's outer container div
     * @property {Element} gridContainer
     */
    _defineProperty(this, "gridContainer", void 0);
    /**
     * the unique column id of the last focused element in the grid
     * @property {string} lastFocusedControlColumnId
     */
    _defineProperty(this, "lastFocusedControlColumnId", void 0);
    /**
     * the row element containing the last focused element in the grid
     * @property {Element} lastFocusedRow
     */
    _defineProperty(this, "lastFocusedControlRow", void 0);
    /**
     * to be executed on AG Grid's first content rendered event
     * @param {Object} gridEvent AG grid firstDataRendered event
     */
    _defineProperty(this, "initialize", function (gridEvent) {
      this.gridContainer = gridEvent.api.gridOptionsWrapper.eGridDiv;
      // block AG grid's tab event listeners in the header
      this.blockAgTabLogic(true);
      // block AG grid's focusin events everywhere
      this.gridContainer.addEventListener('focusin', this.focusInHandler);
      this.gridContainer.addEventListener('keydown', this.keyDownHandler);
      this.setFocusElToFirstHeaderCell();
    }.bind(this));
    /**
     * to be called on AG Grid onComponentStateChanged event
     * update pointer to grid container which may have changed
     * and reapply logic that is lost on most rerenders
     * @param {Object} gridEvent AG Grid onComponentStateChanged event
     * @param {Object} userEvent
     */
    _defineProperty(this, "update", function (gridEvent) {
      this.gridContainer = gridEvent.api.gridOptionsWrapper.eGridDiv;
      this.resetContentTabIndicesDebounced();
      this.blockAgTabLogic();
    }.bind(this));
    _defineProperty(this, "resetContentTabIndicesDebounced", lodash.debounce(this.resetContentTabIndices).bind(this));
    /**
     * tracks the active tabbable element within the grid
     * @param {Object} e native focusin event
     */
    _defineProperty(this, "focusInHandler", function (e) {
      var inGrid = this.gridContainer.contains(e.target);
      if (this.focusEl && inGrid) {
        this.focusEl.tabIndex = -1;
      }
      this.focusEl = e.target;
      e.target.tabIndex = 0;
      // squash the event
      // to prevent AG grid handlers from placing focus incorrectly
      e.stopPropagation();
      if (e.target.matches(this.controlSelector)) {
        var rowSelector = '[role=row]';
        var cellSelector = '[aria-colindex]';
        this.lastFocusedControlColumnId = e.target.closest(cellSelector).getAttribute('aria-colindex');
        this.lastFocusedControlRow = e.target.closest(rowSelector);
      }
      this.blockAgTabLogic();
    }.bind(this));
    /**
     * row selection event handler
     * handles selection state including indeterminate state of select-all checkbox
     * and prevents loss of focus
     * @param {Object} e AG Grid rowSelected event
     */
    _defineProperty(this, "rowSelectionHandler", function (e) {
      this.fixSelectAllCheckboxState(e);
      this.refocusProperControl(e);
    }.bind(this));
    /**
     * sets data attribute for partial row selection
     * leveraged in CSS to show indeterminate state of select-all checkbox
     * @param {Object} e AG Grid rowSelected event
     */
    _defineProperty(this, "fixSelectAllCheckboxState", function (e) {
      var rowCount = e.api.clientSideRowModel.rowsToDisplay.length;
      var selectedRowCount = e.api.clientSideRowModel.selectionService.getSelectedRows().length;
      var partialRowSelection = rowCount && selectedRowCount && selectedRowCount < rowCount;
      this.gridContainer.dataset.partialRowSelection = partialRowSelection;
    }.bind(this));
    /**
     * restores focus properly after rerender
     * currently only required after rowSelected event
     * particularly when user uses the select-all checkbox
     */
    _defineProperty(this, "refocusProperControl", function () {
      var _this = this;
      if (this.focusEl && !document.body.contains(this.focusEl) && this.lastFocusedControlColumnId && this.lastFocusedControlRow) {
        // wait for next thread as grid body rerenders
        setTimeout(function () {
          // locate the control to regain focus
          var cellSelector = "[aria-colindex=\"".concat(_this.lastFocusedControlColumnId, "\"]");
          var selectAllHeaderCellSelector = ".ag-header-cell[aria-colindex='1']";
          var selectAllCheckboxSelector = '.IlDataTableV3--header-checkbox input';
          // the original control causing the rerender and its parent cell
          // probably are no longer in the DOM
          // the row is retained, so find the new cell matching the old one
          var cell = _this.lastFocusedControlRow.querySelector(cellSelector);
          var control;
          if (_this.focusEl.matches(selectAllHeaderCellSelector)) {
            // ag grid focuses the header cell briefly
            // rather than the select-all checkbox when it's clicked
            // prior to losing focus entirely upon rerender
            // put focus on the control where it belongs
            control = cell.querySelector(selectAllCheckboxSelector);
          } else if (_this.focusEl.className) {
            // else if we have a className for our control,
            // find it that way
            var _cell$getElementsByCl = cell.getElementsByClassName(_this.focusEl.className);
            var _cell$getElementsByCl2 = _slicedToArray(_cell$getElementsByCl, 1);
            control = _cell$getElementsByCl2[0];
          } else {
            // otherwise fall back to a tag name
            control = cell.querySelector(_this.focusEl.tagName);
          }
          if (control) {
            control.focus();
          }
        });
      }
    }.bind(this));
    /**
     * stops propagation of tab keydown event
     * a means of last resort where AG configurations are limited
     * @param {Object} e native keydown event
     */
    _defineProperty(this, "stopTabEvent", function (e) {
      if (e.key === 'Tab') {
        // for tab key only
        // squash the keydown event
        // to prevent AG grid handlers from placing focus incorrectly}
        e.stopPropagation();
      }
    });
    /**
     * focus and blur controls within cells via enter and escape key
     * @param {Object} e native key down event
     */
    _defineProperty(this, "keyDownHandler", function (e) {
      var cellSelector = '.ag-cell, .ag-header-cell';
      if (e.target.matches(cellSelector)) {
        var controls = e.target.querySelectorAll(this.controlSelector);
        if (e.key === 'Enter') {
          _toConsumableArray(controls).forEach(function (control) {
            var visible = !!control.clientHeight;
            if (visible) {
              control.focus();
            }
          });
        }
      } else if (e.target.matches(this.controlSelector)) {
        if (e.key === 'Escape') {
          var cell = e.target.closest(cellSelector);
          if (cell) {
            cell.focus();
          }
        }
      }
    }.bind(this));
    _defineProperty(this, "destructor", function () {
      if (this.gridContainer) {
        this.gridContainer.removeEventListener('focusin', this.focusInHandler);
        this.gridContainer.removeEventListener('keydown', this.keyDownHandler);
        this.blockAgTabLogic(false, true);
        this.blockAgTabLogic(true, true);
      }
    }.bind(this));
  }
  return _createClass(AgGridNavigationAdapter, [{
    key: "blockAgTabLogic",
    value:
    /**
     * prevent incorrect AG tab logic
     * grids must be a single tab stop per WAI WCAG requirments above
     * @param {Boolean} header apply fix to the header if true, otherwise body
     */
    function blockAgTabLogic(header, removeOnly) {
      var headerRowSelector = '.ag-header-row';
      var firstBodyRowSelector = '.ag-row-first';
      var selector = header === true ? headerRowSelector : firstBodyRowSelector;
      var rows = this.gridContainer.querySelectorAll(selector);
      // there may be up to six relevant rows
      // main, pinned left, and pinned right in the header
      // main, pinned left, and pinned right in the first row of the body
      // all require some love to prevent the default tab logic
      // within, into, and out of the header
      // note that we cannot simply add a listener to the gridContainer
      // since it is too high in the DOM to intercept the event
      // and prevent the incorrect behavior in AG handlers
      for (var rowCounter = 0, stop = rows.length; rowCounter < stop; rowCounter++) {
        rows[rowCounter].removeEventListener('keydown', this.stopTabEvent);
        if (removeOnly !== true) {
          rows[rowCounter].addEventListener('keydown', this.stopTabEvent);
        }
      }
    }

    /**
     * per WCAG grid requirements
     * maintain only one tabbable element within the grid
     * based on users last navigation within it
     */
  }, {
    key: "resetContentTabIndices",
    value: function resetContentTabIndices() {
      // grids must be a single tab stop
      // set all cells and controls to a tabindex of -1
      _toConsumableArray(this.gridContainer.querySelectorAll(this.controlSelector)).forEach(function (control) {
        control.tabIndex = -1;
      });

      // if the user has clicked into the grid
      // make our tabbable element the cell or control clicked
      if (this.gridContainer.contains(document.activeElement)) {
        this.focusEl = document.activeElement;
      }

      // if we have no tabbable element
      // default to the first header cell
      if (!this.focusEl || !document.body.contains(this.focusEl)) {
        this.setFocusElToFirstHeaderCell();
      }
      this.focusEl.tabIndex = 0;
      this.blockAgTabLogic(true);
    }
  }, {
    key: "setFocusElToFirstHeaderCell",
    value:
    /**
     * establish our initial cell to receive focus
     */
    function setFocusElToFirstHeaderCell() {
      var firstHeaderCell = this.gridContainer.querySelector('.ag-header-cell');
      this.focusEl = firstHeaderCell;
      this.focusEl.tabIndex = 0;
    }
  }]);
}();

export { AgGridNavigationAdapter as default };
